/**
 * Simple in-memory cache for API responses
 * Implements TTL (Time To Live) for cache expiration
 */

class Cache {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 30 * 1000; // 30 seconds default (reduced from 5 minutes)
  }

  /**
   * Generate cache key from URL and params
   */
  generateKey(url, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .map((key) => `${key}=${JSON.stringify(params[key])}`)
      .join("&");
    return `${url}${sortedParams ? `?${sortedParams}` : ""}`;
  }

  /**
   * Get cached value
   */
  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;

    // Check if expired
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }

    return item.value;
  }

  /**
   * Set cache value
   */
  set(key, value, ttl = this.defaultTTL) {
    this.cache.set(key, {
      value,
      expiry: Date.now() + ttl,
    });
  }

  /**
   * Delete cache entry
   */
  delete(key) {
    this.cache.delete(key);
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
  }

  /**
   * Clear expired entries
   */
  clearExpired() {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (now > item.expiry) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache size
   */
  size() {
    return this.cache.size;
  }
}

// Singleton instance
const cache = new Cache();

// Clean up expired entries every 10 minutes
setInterval(() => {
  cache.clearExpired();
}, 10 * 60 * 1000);

export default cache;

